import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useIntl } from 'react-intl';
import { useAuth0 } from '@auth0/auth0-react';
import { useHistory, Link } from 'react-router-dom';
import {
  getUserCustomerAccess,
  shouldShowCustomerSelector,
  getCustomerOptions,
  setActiveCustomer,
  hasPermission,
  PERMISSIONS
} from '../../_metronic/helpers/UserRoleHelpers';
import { useCustomer } from '../contexts/CustomerContext';

export const SoftBlockWarning: React.FC = () => {
  const intl = useIntl();
  const { user } = useAuth0();
  const history = useHistory();
  const { refetch: refetchCustomer } = useCustomer();
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [hasMultipleCustomers, setHasMultipleCustomers] = useState(false);
  const [customerOptions, setCustomerOptions] = useState<Array<{ label: string; value: string }>>([]);
  const [unblocking, setUnblocking] = useState(false);
  const [unblockSuccess, setUnblockSuccess] = useState(false);
  const [unblockError, setUnblockError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      const access = getUserCustomerAccess(user);
      const currentCustomer = window.localStorage.getItem('activeCustomer') || access.primaryCustomer || access.customers[0] || '';
      setSelectedCustomer(currentCustomer);

      if (shouldShowCustomerSelector(user)) {
        setHasMultipleCustomers(true);
        setCustomerOptions(getCustomerOptions(user));
      }
    }
  }, [user]);

  const handleCustomerChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setActiveCustomer(e.target.value);
    history.go(0);
  };

  const handleRequestUnblock = async () => {
    try {
      setUnblocking(true);
      setUnblockError(null);
      const response = await axios.post('/billing/request-unblock');
      if (response.data?.wasUnblocked) {
        setUnblockSuccess(true);
        await refetchCustomer();
        setTimeout(() => {
          window.location.href = '/';
        }, 2000);
      } else {
        const errorId = response.data?.reason === 'overdue'
          ? 'billing.unblock.error-overdue'
          : 'billing.unblock.error';
        const errorDefault = response.data?.reason === 'overdue'
          ? 'Ainda existem cobranças em aberto. Regularize suas pendências para liberar o acesso.'
          : 'Não foi possível verificar o desbloqueio. Tente novamente.';
        setUnblockError(intl.formatMessage({ id: errorId, defaultMessage: errorDefault }));
      }
    } catch (err: any) {
      setUnblockError(
        err.response?.data?.message ||
        intl.formatMessage({
          id: 'billing.unblock.error',
          defaultMessage: 'Não foi possível verificar o desbloqueio. Tente novamente.',
        })
      );
    } finally {
      setUnblocking(false);
    }
  };

  return (
    <div className="d-flex flex-column flex-root">
      <div className="d-flex flex-column flex-center flex-column-fluid p-10">
        <div className="text-center">
          <div className="mb-10">
            <i className="bi bi-exclamation-circle text-danger" style={{ fontSize: '8rem' }}></i>
          </div>

          <h1 className="fw-bold mb-5">
            {intl.formatMessage({
              id: 'soft-block.title',
              defaultMessage: 'Acesso Suspenso',
            })}
          </h1>

          <div className="fw-semibold fs-3 text-gray-600 mb-10 px-5" style={{ maxWidth: '600px', margin: '0 auto' }}>
            {intl.formatMessage({
              id: 'soft-block.message',
              defaultMessage:
                'O acesso à sua conta foi temporariamente suspenso por pendências financeiras. Regularize sua situação para que o acesso seja liberado automaticamente.',
            })}
          </div>

          <div className="card shadow-sm" style={{ maxWidth: '500px', margin: '0 auto' }}>
            <div className="card-body p-8">
              <h3 className="card-title mb-5">
                {intl.formatMessage({
                  id: 'soft-block.contact-title',
                  defaultMessage: 'Regularize sua situação:',
                })}
              </h3>

              <div className="d-flex flex-column gap-4">
                <div className="d-flex align-items-center">
                  <div className="symbol symbol-40px me-4">
                    <div className="symbol-label bg-light-primary">
                      <i className="bi bi-envelope-fill text-primary fs-2"></i>
                    </div>
                  </div>
                  <div className="flex-grow-1">
                    <div className="text-gray-800 fw-bold fs-6">Email</div>
                    <a href="mailto:financeiro@konfidency.com.br" className="text-primary fw-semibold">
                      financeiro@konfidency.com.br
                    </a>
                  </div>
                </div>

                <div className="d-flex align-items-center">
                  <div className="symbol symbol-40px me-4">
                    <div className="symbol-label bg-light-success">
                      <i className="bi bi-whatsapp text-success fs-2"></i>
                    </div>
                  </div>
                  <div className="flex-grow-1">
                    <div className="text-gray-800 fw-bold fs-6">WhatsApp</div>
                    <a
                      href="https://wa.me/551151980535"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-success fw-semibold"
                    >
                      (11) 5198-0535
                    </a>
                  </div>
                </div>

                <div className="d-flex align-items-center">
                  <div className="symbol symbol-40px me-4">
                    <div className="symbol-label bg-light-info">
                      <i className="bi bi-chat-dots-fill text-info fs-2"></i>
                    </div>
                  </div>
                  <div className="flex-grow-1">
                    <div className="text-gray-800 fw-bold fs-6">
                      {intl.formatMessage({
                        id: 'soft-block.support-portal',
                        defaultMessage: 'Portal de Suporte',
                      })}
                    </div>
                    <a
                      href="https://help.konfidency.com.br"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-info fw-semibold"
                    >
                      help.konfidency.com.br
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="text-muted fs-6 mt-8">
            {intl.formatMessage({
              id: 'soft-block.footer',
              defaultMessage: 'O acesso será liberado automaticamente em até 24 horas após a regularização de todas as pendências financeiras.',
            })}
          </div>

          {user && window.localStorage.getItem("activeCustomer") &&
           hasPermission(user, window.localStorage.getItem("activeCustomer") || "", PERMISSIONS.MANAGE_SETTINGS) && (
            <div className="mt-10 d-flex flex-column align-items-center gap-4">
              <div className="d-flex gap-3">
                <button
                  type="button"
                  className="btn btn-success btn-lg"
                  onClick={handleRequestUnblock}
                  disabled={unblocking || unblockSuccess}
                >
                  {unblocking ? (
                    <span className="spinner-border spinner-border-sm me-2"></span>
                  ) : (
                    <i className="bi bi-unlock me-2"></i>
                  )}
                  {intl.formatMessage({
                    id: 'billing.unblock.button',
                    defaultMessage: 'Desbloquear Agora',
                  })}
                </button>
                <Link to="/billing/invoices" className="btn btn-light-primary btn-lg">
                  <i className="bi bi-credit-card me-2"></i>
                  {intl.formatMessage({
                    id: 'soft-block.view-invoices',
                    defaultMessage: 'Ver Minhas Cobranças',
                  })}
                </Link>
              </div>
              {unblockSuccess && (
                <div className="text-success fw-bold fs-6">
                  <i className="bi bi-check-circle me-1"></i>
                  {intl.formatMessage({
                    id: 'billing.unblock.success',
                    defaultMessage: 'Acesso restaurado com sucesso! Redirecionando...',
                  })}
                </div>
              )}
              {unblockError && (
                <div className="text-danger fw-semibold fs-6">
                  <i className="bi bi-exclamation-circle me-1"></i>
                  {unblockError}
                </div>
              )}
            </div>
          )}
        </div>

        {hasMultipleCustomers && (
          <div className="mb-8 mt-10" style={{ maxWidth: '400px', margin: '0 auto' }}>
            <div className="alert alert-info d-flex align-items-center">
              <i className="bi bi-info-circle-fill me-2"></i>
              <div className="flex-grow-1">
                {intl.formatMessage({
                  id: 'soft-block.switch-account',
                  defaultMessage: 'Você tem acesso a outras contas. Selecione uma conta ativa:',
                })}
              </div>
            </div>
            <select
              className="form-select form-select-lg"
              value={selectedCustomer}
              onChange={handleCustomerChange}
            >
              {customerOptions.map((option, index) => (
                <option key={index} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
    </div>
  );
};
